/**
 * DeliveryMetricsRecalcCard
 *
 * Admin-only maintenance control: recompute OTD (GRN on-time-status) and OTIF
 * for the whole company from the current GRN->schedule allocations. The recalc
 * runs in the background; this card polls the status endpoint and alerts the
 * admin (toast + on-card banner) when it completes, even across reloads.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Loader2, RefreshCw, CheckCircle2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { importsAPI, DeliveryMetricsRecalcStatus } from '../../../lib/api';
import { useFormatters } from '../../../contexts/PreferencesContext';

const POLL_MS = 4000;

export function DeliveryMetricsRecalcCard() {
  const [status, setStatus] = useState<DeliveryMetricsRecalcStatus | null>(null);
  const [triggering, setTriggering] = useState(false);
  const { formatDateTime } = useFormatters();
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const wasRunning = useRef(false);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const fetchStatus = useCallback(async () => {
    try {
      const s = await importsAPI.getDeliveryMetricsRecalcStatus();
      setStatus(s);
      if (s.status === 'running') {
        wasRunning.current = true;
      } else if (wasRunning.current && (s.status === 'completed' || s.status === 'failed')) {
        // Transitioned out of running -> alert once.
        wasRunning.current = false;
        stopPolling();
        if (s.status === 'completed') {
          const n = s.result?.updated;
          toast.success(
            `OTIF & OTD recalculation complete${typeof n === 'number' ? ` — ${n} lines updated` : ''}.`,
          );
        } else {
          toast.error(`Recalculation failed: ${s.error || 'unknown error'}`);
        }
      }
    } catch (err: unknown) {
      console.error('Failed to fetch recalc status:', err);
    }
  }, [stopPolling]);

  const startPolling = useCallback(() => {
    stopPolling();
    pollRef.current = setInterval(fetchStatus, POLL_MS);
  }, [fetchStatus, stopPolling]);

  // Initial load: read status; if a run is already in progress, resume polling.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const s = await importsAPI.getDeliveryMetricsRecalcStatus();
        if (cancelled) return;
        setStatus(s);
        if (s.status === 'running') {
          wasRunning.current = true;
          startPolling();
        }
      } catch (err: unknown) {
        console.error('Failed to load recalc status:', err);
      }
    })();
    return () => {
      cancelled = true;
      stopPolling();
    };
  }, [startPolling, stopPolling]);

  const handleTrigger = async () => {
    setTriggering(true);
    try {
      const s = await importsAPI.triggerDeliveryMetricsRecalc();
      setStatus(s);
      wasRunning.current = true;
      toast.info('Recalculation started — you\'ll be alerted when it finishes.');
      startPolling();
    } catch (err: unknown) {
      const e = err as { response?: { status?: number; data?: { detail?: string } } };
      if (e.response?.status === 409) {
        toast.info('A recalculation is already running.');
        wasRunning.current = true;
        startPolling();
      } else {
        toast.error('Could not start recalculation.');
      }
    } finally {
      setTriggering(false);
    }
  };

  const running = status?.status === 'running';
  const fmt = (iso?: string | null) => (iso ? formatDateTime(iso) : '');

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <RefreshCw className="h-4 w-4" />
          Recalculate OTIF &amp; OTD
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Recompute on-time delivery (OTD) and OTIF for every line from the current
          GRN-to-schedule allocations. Runs in the background; you'll be alerted when
          it completes.
        </p>

        <Button onClick={handleTrigger} disabled={triggering || running}>
          {(triggering || running) ? (
            <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Recalculating…</>
          ) : (
            <><RefreshCw className="mr-2 h-4 w-4" />Recalculate now</>
          )}
        </Button>

        {running && status?.started_at && (
          <div className="flex items-center gap-2 rounded-md bg-blue-50 px-3 py-2 text-sm text-blue-800">
            <Loader2 className="h-4 w-4 animate-spin" />
            In progress — started {fmt(status.started_at)}
          </div>
        )}
        {status?.status === 'completed' && (
          <div className="flex items-center gap-2 rounded-md bg-green-50 px-3 py-2 text-sm text-green-800">
            <CheckCircle2 className="h-4 w-4" />
            Last completed {fmt(status.finished_at)}
            {typeof status.result?.updated === 'number' && ` — ${status.result.updated} lines updated`}
          </div>
        )}
        {status?.status === 'failed' && (
          <div className="flex items-center gap-2 rounded-md bg-red-50 px-3 py-2 text-sm text-red-800">
            <AlertCircle className="h-4 w-4" />
            Last run failed: {status.error || 'unknown error'}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
